/**
 * 侧栏导航的**数据层**(2026-07-24 F3 从 `AppSidebar.tsx` 拆出)。
 *
 * ★拆文件的理由不是「行数多」:组件文件里混着导出常量会破坏 react-refresh
 * (eslint `react-refresh/only-export-components` 一直在报,本轮拆组又多了 4 个导出)。
 * 数据层单独成文件后,`AppSidebar.tsx` 只导出组件,HMR 恢复正常,顺带把 690 行拆成两个可读单元。
 */
import {
  BarChart3,
  BookOpen,
  Boxes,
  Building2,
  ClipboardList,
  Clapperboard,
  CircleHelp,
  Coins,
  CreditCard,
  Film,
  Flame,
  FolderOpen,
  GraduationCap,
  Headphones,
  History,
  Image as ImageIcon,
  LayoutDashboard,
  Link2,
  Newspaper,
  Palette,
  Radar,
  Receipt,
  ScrollText,
  Send,
  Settings,
  Shield,
  BookOpenCheck,
  Sparkles,
  UserRound,
  Users,
  Wallet,
  Handshake,
  KeyRound,
  Server,
  UserCog,
  CalendarClock,
  ShieldCheck,
  ShieldAlert,
} from "lucide-react";

import { DEPT_TITLE } from "@/entities/work/model/deptTitles";
import {
  PARTNER_PAGES,
  partnerCan,
  type PartnerPageKey,
  type PartnerPerm,
} from "@/entities/partner";
import { ROLES, type RoleKey } from "@/shared/lib/rbac";
import { getBrand } from "@/shared/brand/store";
import { showsReleaseNotes } from "@/shared/brand/bootstrap";
import type { Scope } from "@/contexts/AuthContext";

export interface NavItem {
  /** 后端 role_module_permissions.module_key,决定显隐;dashboard 永远可见,不写。 */
  moduleKey?: string;
  title: string;
  /** i18next key (common.nav.*); 可选, 缺省回退到 title。 */
  titleKey?: string;
  url: string;
  icon: typeof Headphones;
  /**
   * V11.4 (2026-05-12) — 可选 scope 白名单。设了就只有匹配 scope 的用户能看见,
   * 没 moduleKey 但 scope 受限的 item 用这个表达(如 "品牌资产"是租户专属,
   * 平台超管 scope=PLATFORM 不该看到)。
   */
  scopes?: Scope[];
  /**
   * V22 (2026-05-15) — 可选 role 白名单。设了就只有匹配 role 的用户能看见,
   * 用于 STAFF 隐藏 Billing/套餐入口(用户决策 7:员工只看"能用"不看成本)。
   */
  roles?: RoleKey[];
  /**
   * 产品注册表(2026-06-04) — 仅平台超管"代客登录"(impersonation)时可见。
   * 后端 /platform/products 端点 @sec.isImpersonating() 门控,无独立 module_key,
   * 故走这个独立 flag 而非 moduleKey gate。
   */
  impersonateOnly?: boolean;
  /** 即将上线愿景位(catalog 驱动):灰化 + badge,收编在「AI 能力」组尾部。 */
  comingSoon?: boolean;
  /**
   * OEM W5 私有化:只在本实例 license 启用(GET /platform/license 的 mode ≠ DISABLED)时出现。
   * visibleByPerms 缺省按「未启用」藏 —— SaaS 上只能直链进。
   */
  licenseOnly?: boolean;
  /** OEM 第二批 A4:渠道后台页要的权限点;AppSidebar 按 /me 的 partnerPerms 再筛一道。 */
  partnerPerm?: PartnerPerm;
  /** 1002:入口旁的小标(如客服舱「已停用 · 仅人工处理」)。 */
  badge?: string;
  /** 1004 O5:只在直营品牌出现(「更新记录」露我方版本号与发版说明,渠道租户藏起来)。 */
  directBrandOnly?: boolean;
  /**
   * 1004 第三轮:代客中隐藏(个人中心 = 平台超管本人的资料,代客时不该出现,更不该能改)。
   * 直开路由由页面自己给只读说明,api 侧同样拒写。
   */
  hideWhenImpersonating?: boolean;
}

type NavModules = Record<string, { page: string }>;

/**
 * 1002:客服舱没开但「历史上拥有过」(everOwnedModules;老后端无该字段时回落 pausedModules)→ 侧栏照给客服入口,
 * 进去是「只读 + 人工处理」模式。从没拥有过的不出。返回给 visibleByPerms 用的 modules 与是否需挂「已停用」小标。
 */
export function kefuNavOverride<M extends NavModules>(
  modules: M | undefined,
  perms: { everOwnedModules?: string[]; pausedModules?: string[] } | null | undefined
): { modules: M | undefined; kefuOff: boolean } {
  if (!modules) return { modules, kefuOff: false };
  const cur = modules["ai_kefu"];
  if (cur && cur.page !== "none") return { modules, kefuOff: false };
  const owned = perms?.everOwnedModules ?? perms?.pausedModules ?? [];
  if (!owned.includes("ai_kefu")) return { modules, kefuOff: false };
  return {
    modules: { ...modules, ai_kefu: { ...(cur ?? {}), page: "view" } } as M,
    kefuOff: true,
  };
}

// ── 经营增长价值主线 IA(2026-07-05 重排)：看盘(驾驶舱) → 诊断(军师/经营诊断) → 派活(内容/客户) → 复盘 ──
// 分组按「军师主导的经营闭环」重排;每项的 moduleKey/roles/scopes gate 逐字保留(只换所在组 + 组 label)。

/**
 * 【派单闭环】团队任务(建单 + 企微下发)· 派单回看(状态/完成率复盘)—— 归**组织**支撑区。
 *
 * ★2026-07-29(V25 军师回归)三分家:军师的三类东西各归其位,不再混作一个「军师」侧栏组。
 *   - **结果型**(经营诊断 / 经营看板)→ 情报决策群卡墙(后端 catalog 挂 cluster)。
 *   - **配置型**(记忆 / 技能 / 两项审计)→ 小阳主页 `/advisor` tab。
 *   - **管理动作**(本组)→ 组织支撑区:派活是人和任务的事,不是一项 AI 能力,上卡墙会喧宾夺主。
 * ★随之删掉 `advisorItems` / `cockpitItems` / `diagnosisItems`:租户端 2026-07-24 起走
 *   TenantShellNav(轨 + 面板),那三组只挂在 AppSidebar 的 tenantTree 上,而 tenantTree 只有
 *   平台超管 scope 才渲染 —— 已是零消费的死数据(军师全线不可达的直接原因之一)。
 */
export const dispatchItems: NavItem[] = [
  {
    title: "团队任务",
    titleKey: "nav.teamTasks",
    url: "/team-tasks",
    icon: ClipboardList,
    roles: [ROLES.TENANT_ADMIN, ROLES.TENANT_MANAGER],
  },
  {
    title: "派单回看",
    titleKey: "nav.dispatchReview",
    url: "/advisor-dispatches",
    icon: Send,
    roles: [ROLES.TENANT_ADMIN, ROLES.TENANT_MANAGER],
  },
];

/**
 * 【创作】(2026-07-24 F3 IA 二次收口):原「AI 能力」9 项拆成**创作 4 项** + **服务与洞察 3 项**。
 *
 * ★拆的依据不是数量,是种类:生成器(做出东西来)与服务/洞察(处理客户、看数据)不是一类活。
 * 拆完之后侧栏的「创作」正好对上工作台的部门卡墙与各 hub 页 —— 三处同名同集合。
 *
 * ★名字走 `DEPT_TITLE` 单源(entities/work/model/deptTitles):此前侧栏叫「AI海报」、
 * hub 页头叫「创意设计中心」、部门卡也叫「创意设计中心」—— 同一个东西两个名字,
 * 而没有任何东西拦着它们继续漂。改这一处,三处一起变。
 *
 * ★「详情长图」`/ai-haibao/detail-long` 撤出侧栏(**路由保留**,深链/书签不破):
 * 它是海报 hub 里 10 张长图卡的落地页,不是与海报并列的功能。
 *
 * 每项 moduleKey/roles/scopes gate **逐字保留**,只换所在组与显示名。
 */
export const creationItems: NavItem[] = [
  {
    moduleKey: "ai_poster",
    title: DEPT_TITLE.poster,
    url: "/ai-haibao",
    icon: ImageIcon,
  },
  {
    moduleKey: "ai_video",
    title: DEPT_TITLE.video,
    url: "/ai-video",
    icon: Film,
  },
  {
    moduleKey: "ai_digital_human",
    title: DEPT_TITLE.dh,
    url: "/ai-digital-human",
    icon: Clapperboard,
  },
  // 爆款情报:横切输入源(解析→蓝图→路由到视频/分身/海报)。挂在被喂养的三个生成器之后
  // ——「喂养非包含」,直接创作仍是主路径。
  {
    moduleKey: "ai_explosive_parse",
    title: DEPT_TITLE.explosive,
    url: "/ai-explosive",
    icon: Flame,
  },
  // 官媒舱(R2-T2 接线):软文出稿是生成器,归创作组尾部(输入型追爆舱之后)。
  {
    moduleKey: "ai_reporter",
    title: DEPT_TITLE.jizhe,
    url: "/ai-jizhe",
    icon: Newspaper,
  },
];

/** 【服务与洞察】原「AI 能力」里的非生成器:处理客户 / 看数据。「即将上线」愿景位收在本组尾部。 */
export const serviceItems: NavItem[] = [
  {
    moduleKey: "ai_kefu",
    title: "客服舱",
    titleKey: "nav.aiKefu",
    url: "/ai-kefu",
    icon: Headphones,
  },
  {
    moduleKey: "ai_peilian",
    title: "陪练舱",
    titleKey: "nav.aiPeilian",
    url: "/ai-peilian",
    icon: GraduationCap,
  },
  {
    moduleKey: "geo_radar",
    title: "GEO雷达",
    titleKey: "nav.geoRadar",
    url: "/geo-radar",
    icon: Radar,
  },
];

/** 平台侧「内容运维」:admin 端海报模板/场景管理。PLATFORM_ADMIN 只有 poster_admin,看不到 ai_poster。 */
export const platformContentItems: NavItem[] = [
  {
    moduleKey: "poster_admin",
    title: "海报模板",
    url: "/platform/poster-admin/templates",
    icon: ImageIcon,
  },
  {
    title: "BGM 管理",
    titleKey: "nav.videoBgmAdmin",
    url: "/platform/video-bgms",
    icon: Film,
    scopes: ["PLATFORM"],
  },
  // 2026-08-09 K-3:知识审核台从「产品候选」路由名下解救出来 —— 此前唯一入口是代客态
  // /products/proposals,名不对文、平台侧无一级入口。页面自带非代客引导(KbReviewDeskPage)。
  {
    title: "知识审核",
    url: "/kb/review",
    icon: BookOpenCheck,
    scopes: ["PLATFORM"],
  },
  // 1011 算法备案:生成内容审核台 + 敏感词库(后端 /platform/aigc-reviews、/platform/sensitive-words)
  {
    title: "生成内容审核",
    url: "/platform/content-review",
    icon: ShieldCheck,
    scopes: ["PLATFORM"],
  },
  {
    title: "敏感词库",
    url: "/platform/sensitive-words",
    icon: ShieldAlert,
    scopes: ["PLATFORM"],
  },
];

// #9 资产中心(2026-07-04)— 团队资产(品牌资产:logo/二维码/配色/联系方式,租户级共享)。
// gate 逐字保留(scope TENANT + roles ADMIN/MANAGER;PLATFORM 超管不看,STAFF 进会 403)。导出供测试。
export const teamAssetItems: NavItem[] = [
  {
    title: "品牌资产",
    titleKey: "nav.brandAssets",
    url: "/tenant-admin/brand-assets",
    icon: Palette,
    scopes: ["TENANT"],
    roles: [ROLES.TENANT_ADMIN, ROLES.TENANT_MANAGER],
  },
];

// 【数据资产】知识库 · 行业知识库 · 产品 · 品牌资产。品牌资产由 teamAssetItems 拼入(保留其导出与 gate)。
const baseDataAssetItems: NavItem[] = [
  {
    moduleKey: "knowledge_base",
    title: "知识库管理",
    titleKey: "nav.knowledgeBase",
    url: "/knowledge",
    icon: BookOpen,
  },
  {
    moduleKey: "industry_kb",
    title: "行业知识库",
    titleKey: "nav.industryKb",
    url: "/industry-kb",
    icon: BookOpen,
  },
  // packs W2(2026-07-27)— 人物库(一等库,与产品库并列)。后端 URL 门=租户三角色
  // (与 video/poster/digital-human 同门,无独立 moduleKey):库墙/详情/物料是团队共用创作资产,
  // 全员可见;真人形象与声纹授权入库后全公司共用(录音克隆须本人、跨公司隔离、删除仅本人或管理员)。
  {
    title: "人物",
    titleKey: "nav.characters",
    url: "/characters",
    icon: UserRound,
    scopes: ["TENANT"],
  },
  // 产品注册表 — 租户管理员/经理可见(只读查看 + 建场景绑定);建/改/删仅平台代客(页面内按钮 + 后端双重门控)。
  {
    title: "产品",
    titleKey: "nav.products",
    url: "/products",
    icon: Boxes,
    roles: [ROLES.TENANT_ADMIN, ROLES.TENANT_MANAGER],
  },
];
/**
 * 【作品与资产】(F3 新组)= 我的作品 + 品牌资产 + 数据资产(含人物/产品两个一等库)。
 *
 * ★「我的作品」`/works` 是本轮建的三线聚合页,也是这一组存在的理由:此前**没有**
 * 「我全部作品」的落点(作品各埋在三个 hub 页底部)。
 * ★「我的资产」(形象+声纹)packs W2 收编进人物库(设计 §2.4):导航项撤下,
 * 旧路由 /ai-digital-human/my-assets 重定向 /characters,页面并入人物详情「真人原料」Tab。
 * gate 全部逐字保留(`/works` 自己按线降级,不挂单一 moduleKey)。
 */
export const worksAndAssetItems: NavItem[] = [
  {
    title: "我的作品",
    url: "/works",
    icon: FolderOpen,
    scopes: ["TENANT"],
  },
  ...teamAssetItems,
  ...baseDataAssetItems,
];

// 【组织与账户】用户 · 组织 · 权限 · 租户设置 · 权益/套餐 · 计费(合并零散管理项)。
export const orgItems: NavItem[] = [
  {
    moduleKey: "tenant_users",
    title: "用户管理",
    titleKey: "nav.tenantUsers",
    url: "/users",
    icon: Users,
    roles: [ROLES.TENANT_ADMIN, ROLES.TENANT_MANAGER, ROLES.PLATFORM_ADMIN],
  },
  // S1 (2026-06-27) — 组织管理(部门树 + 成员归属/负责人)。组织底座是租户内建 → scope=TENANT 门,
  // 平台反馈条1(2026-07-05):非代客平台超管(scope=PLATFORM)不需要组织管理,scope 门挡掉;
  // 代客态 effectiveRole=TENANT_ADMIN→scope=TENANT 正常放行。role 门 STAFF 隐藏;MANAGER 只读。
  {
    title: "组织管理",
    titleKey: "nav.orgDepartments",
    url: "/org-departments",
    icon: Users,
    scopes: ["TENANT"],
    roles: [ROLES.TENANT_ADMIN, ROLES.TENANT_MANAGER],
  },
  // 2026-09-16 — 租户自己的操作审计。ADMIN-only:审计里有「谁看了什么、谁授了谁权」,本身就敏感。
  {
    title: "操作审计",
    titleKey: "nav.tenantAudit",
    url: "/tenant-audit",
    icon: Shield,
    scopes: ["TENANT"],
    roles: [ROLES.TENANT_ADMIN],
  },
  {
    moduleKey: "permission_matrix",
    title: "权限管理",
    titleKey: "nav.permission",
    url: "/permissions",
    icon: Shield,
  },
  {
    moduleKey: "system_settings",
    title: "租户设置",
    titleKey: "nav.tenantSettings",
    url: "/tenant-settings",
    icon: Settings,
  },
  // 渠道管理(2026-07-11 IA):AI客服/AI陪练迁入「AI 能力」后客户经营组只剩它一项,
  // 本质是企微渠道的配置管理 → 归入组织与账户,紧邻租户设置。gate 逐字保留。
  {
    moduleKey: "channel_management",
    title: "渠道管理",
    titleKey: "nav.channelMgmt",
    url: "/channels",
    icon: Link2,
  },
  // 我的套餐 = TENANT_ADMIN-only(后端 TenantBillingController.me() @PreAuthorize('TENANT_ADMIN'))。MANAGER 收紧不给(避免 403 破页)。
  {
    moduleKey: "my_billing",
    title: "我的套餐",
    titleKey: "nav.myBilling",
    url: "/my-billing",
    icon: CreditCard,
    roles: [ROLES.TENANT_ADMIN, ROLES.PLATFORM_ADMIN],
  },
  // 我的积分(2026-09-02 积分明细透明化批):员工/经理自查扣费记录。超管走「我的套餐」→
  // 「积分明细」看全租户,不重复给这一条(role 门与 my_billing 互斥)。
  {
    moduleKey: "my_credits",
    title: "我的积分",
    titleKey: "nav.myCredits",
    url: "/my-credits",
    icon: Coins,
    roles: [ROLES.TENANT_STAFF, ROLES.TENANT_MANAGER],
  },
];

/**
 * 平台侧【计费】:财务看板 / 账目流水。
 * ★它们本来就是**运维直入**的平台能力,却一直挂在租户的「组织与账户」组里 —— 靠 moduleKey
 * 门挡着租户看不到。位置错了不等于门错了:门保持逐字不动,只把它们挪到平台树该在的地方。
 * (调用模板 billing_dim_call_template 故意仍不进侧栏:误改影响面大,走 DBA。)
 */
const platformBillingItems: NavItem[] = [
  {
    moduleKey: "finance_dashboard",
    title: "财务看板",
    titleKey: "nav.financeBoard",
    url: "/admin/billing-dashboard",
    icon: Wallet,
  },
  {
    moduleKey: "billing_ledger",
    title: "账目流水",
    titleKey: "nav.billingLedger",
    url: "/admin/billing-ledger",
    icon: Receipt,
  },
];

// 【平台管理】超管专属:租户 · 套餐 · 接入渠道总览 · 审计 · 平台账号 · LLM 配置。
const platformItems: NavItem[] = [
  {
    moduleKey: "tenant_management",
    title: "租户管理",
    titleKey: "nav.tenantMgmt",
    url: "/tenants",
    icon: Building2,
  },
  // OEM C-I:渠道商管理。无 moduleKey(后端 /platform/partners/** 只认 PLATFORM_ADMIN,不走模块权限表),
  // scopes 钉死 PLATFORM,防它被拼进别的树时对租户 / 渠道露出。
  {
    title: "渠道商",
    titleKey: "nav.platformPartners",
    url: "/platform/partners",
    icon: Handshake,
    scopes: ["PLATFORM"],
  },
  // OEM W5:私有化部署(后端 /platform/private-deployments/** 只认 PLATFORM_ADMIN,同上无 moduleKey)。
  {
    title: "私有化部署",
    titleKey: "nav.platformPrivate",
    url: "/platform/private-deployments",
    icon: Server,
    scopes: ["PLATFORM"],
  },
  {
    moduleKey: "plan_catalog",
    title: "套餐配置",
    titleKey: "nav.planCatalog",
    url: "/tenant-plans",
    icon: CreditCard,
  },
  {
    moduleKey: "platform_channels",
    title: "接入渠道总览",
    titleKey: "nav.platformChannels",
    url: "/platform/channels",
    icon: Link2,
  },
  {
    moduleKey: "platform_audit",
    title: "审计日志",
    titleKey: "nav.platformAudit",
    url: "/platform/audit",
    icon: ScrollText,
  },
  {
    moduleKey: "platform_admins",
    title: "平台账号",
    titleKey: "nav.platformAdmins",
    url: "/platform/admins",
    icon: Shield,
  },
];

/** 平台侧【系统】:LLM 配置(模型目录是它的子页,不单独占一级入口)。 */
const platformSystemItems: NavItem[] = [
  {
    moduleKey: "platform_llm_config",
    title: "LLM 配置",
    titleKey: "nav.llmConfig",
    url: "/platform/llm-config",
    icon: Sparkles,
  },
  // OEM 第二批 A9:平台设置(合规信息:我方 ICP / 公安备案号)。
  {
    title: "平台设置",
    titleKey: "nav.platformSettings",
    url: "/platform/settings",
    icon: Settings,
    scopes: ["PLATFORM"],
  },
  // 1011 算法备案:全局默认合规配置(可见标识 / 敏感词拦截 / 云端内容安全 / 只用国内模型)
  {
    title: "合规配置",
    url: "/platform/compliance",
    icon: Shield,
    scopes: ["PLATFORM"],
  },
  // OEM W5 私有化实例上的「授权」页:licenseOnly = 只有后端说 license 启用才出现(SaaS 上藏起来)。
  {
    title: "授权",
    titleKey: "nav.platformLicense",
    url: "/platform/license",
    icon: KeyRound,
    scopes: ["PLATFORM"],
    licenseOnly: true,
  },
];

/**
 * 【帮助】新手指引 + 更新记录(2026-09-19 §21.1)——顶栏支撑区第 4 项。
 *
 * ★不挂 moduleKey / roles / scopes:帮助是**任何登录用户**都该进得去的地方,
 *   「看不懂怎么用」跟他开通了哪几个舱无关。租户三角色 + 代客态全放行;
 *   平台超管不走这条轨(TenantShellNav 只表达租户轨),故不必额外 gate。
 * ★与 dispatchItems 同一条纪律:定义在这里、只喂 SUPPORT_GROUPS.help,
 *   **不并进任何一棵侧栏树** —— 并进去会让平台超管的侧栏冒出租户帮助项。
 */
export const helpItems: NavItem[] = [
  {
    title: "新手指引",
    titleKey: "nav.helpGuides",
    url: "/help",
    icon: CircleHelp,
  },
  {
    title: "更新记录",
    titleKey: "nav.helpChangelog",
    url: "/help/changelog",
    icon: History,
    directBrandOnly: true,
  },
];

export const personalItems: NavItem[] = [
  // 2026-05-25 — 个人中心进左导航(全员可见,无 module gate;/me/** 是 authenticated 能力)。
  {
    title: "个人中心",
    titleKey: "nav.profile",
    url: "/profile",
    icon: UserRound,
    hideWhenImpersonating: true,
  },
];

// ── IA 重排(2026-07-11):「AI 能力」大类领衔 ──
// 所有 AI 开头的功能收进一个可折叠大类置顶(每日干活入口);数据/我的资产随后;
// 管理配置/平台/个人收尾。
// 顺序是产品叙事的一部分,抽成模块级 SOT 供测试锁死;每项的 moduleKey/roles/scopes gate 不动。
// ── 经营增长组 2026-07-13 下架 → 2026-07-29 解冻(V25 军师回归,老板重拍):不再以「侧栏组」形态
//    回来,而是**经营诊断 / 经营看板两张真卡**进情报决策群卡墙(catalog cluster 单源)。 ──

/**
 * 侧栏两棵树(2026-07-24 F3):**租户树与平台树整棵分开**,不再靠 gate 在同一棵树上叠加过滤。
 *
 * ★为什么分开:一棵树同时服务 4 个角色 × 2 个 scope 时,「这一项到底该不该出现」要靠读三个门
 * (moduleKey / roles / scopes)才说得清,顺序也只能按两边的最大公约数排 —— 平台超管看到的是
 * 一棵为租户排的树,租户看到的是一棵留着平台空位的树。
 * ★门本身**一个字都没动**:分开的是「排列」,不是「授权」。平台树末尾仍保留「管理」组承接
 * 平台超管当前 gate 允许看到的租户侧管理项(如 tenant_users)—— 彻底剥离要先核平台超管的
 * 真实 permissions 集合,那是另一件事,不在本轮冒险。
 *
 * 顺序是产品叙事的一部分,抽成模块级 SOT 供测试锁死。
 */
const tenantTree = [
  // accent = 旗舰款标题(Sparkles + 主色渐变),只给「创作」——它是每天真正干活的入口。
  {
    items: creationItems,
    labelKey: "sidebar.groupCreation",
    shortKey: "sidebar.groupCreationShort",
    accent: true,
  },
  {
    items: worksAndAssetItems,
    labelKey: "sidebar.groupWorksAssets",
    shortKey: "sidebar.groupWorksAssetsShort",
  },
  {
    items: serviceItems,
    labelKey: "sidebar.groupServices",
    shortKey: "sidebar.groupServicesShort",
  },
  { items: orgItems, labelKey: "sidebar.groupOrg", shortKey: "sidebar.groupOrgShort" },
  {
    items: personalItems,
    labelKey: "sidebar.groupPersonal",
    shortKey: "sidebar.groupPersonalShort",
  },
] as const;

const platformTree = [
  {
    items: platformItems,
    labelKey: "sidebar.groupPlatformOps",
    shortKey: "sidebar.groupPlatformOpsShort",
    accent: true,
  },
  {
    items: platformContentItems,
    labelKey: "sidebar.groupPlatformContent",
    shortKey: "sidebar.groupPlatformContentShort",
  },
  {
    items: platformBillingItems,
    labelKey: "sidebar.groupPlatformBilling",
    shortKey: "sidebar.groupPlatformBillingShort",
  },
  {
    items: platformSystemItems,
    labelKey: "sidebar.groupPlatformSystem",
    shortKey: "sidebar.groupPlatformSystemShort",
  },
  { items: orgItems, labelKey: "sidebar.groupOrg", shortKey: "sidebar.groupOrgShort" },
  {
    items: personalItems,
    labelKey: "sidebar.groupPersonal",
    shortKey: "sidebar.groupPersonalShort",
  },
] as const;

const PARTNER_ICONS: Record<PartnerPageKey, typeof Headphones> = {
  overview: LayoutDashboard,
  tenants: Building2,
  credits: Coins,
  usage: BarChart3,
  settlements: Receipt,
  annualFees: CalendarClock,
  wallet: Wallet,
  brand: Palette,
  accounts: UserCog,
};

/**
 * OEM 渠道后台(W3 C-G 骨架)。无 moduleKey:渠道管理员不走租户模块权限,整组对 scope=PARTNER 恒显示;
 * scopes 钉死 PARTNER,防它万一被拼进别的树时对平台/租户露出。清单单源 = entities/partner PARTNER_PAGES。
 */
export const partnerItems: NavItem[] = PARTNER_PAGES.map((p) => ({
  title: p.title,
  titleKey: p.titleKey,
  url: p.url,
  icon: PARTNER_ICONS[p.key],
  scopes: ["PARTNER"],
  partnerPerm: p.requiredPerm,
}));

/**
 * 渠道树再按渠道权限点筛(OEM 第二批 A4)。perms = null(还没拉到 / 老后端)→ 不筛,
 * 服务端照样拦;只是体验层。
 */
export function visibleByPartnerPerms(
  items: NavItem[],
  perms: readonly string[] | null | undefined
): NavItem[] {
  return items.filter((it) => !it.partnerPerm || partnerCan(perms, it.partnerPerm));
}

const partnerTree = [
  {
    items: partnerItems,
    labelKey: "sidebar.groupPartner",
    shortKey: "sidebar.groupPartnerShort",
    accent: true,
  },
  {
    items: personalItems,
    labelKey: "sidebar.groupPersonal",
    shortKey: "sidebar.groupPersonalShort",
  },
] as const;

/** 按 scope 取树;未知 scope(权限还没拉到)回落租户树 —— 它是绝大多数用户的那棵。 */
export function sidebarTreeFor(scope: Scope | undefined) {
  if (scope === "PLATFORM") return platformTree;
  if (scope === "PARTNER") return partnerTree;
  return tenantTree;
}

/** 兼容既有导入(顺序单测按 scope 分别锁两棵树)。 */
export const SIDEBAR_GROUPS = tenantTree;

/**
 * 严格模式: module_key 必须**显式**在 permissions.modules 中且 page !== 'none' 才显示。
 * 默认隐藏 — 平台超管的 permissions 不含 ai_kefu 等租户模块时,这些项就不会出现。
 *
 * 加载中(permissions=null)给个 fallback:已登录但还没拉到时显示空,
 * 避免 flash 一堆"无权限"的菜单——sidebar 短暂空白比闪烁更克制。
 */
export function visibleByPerms(
  items: NavItem[],
  modules: Record<string, { page: string }> | undefined,
  scope?: Scope,
  role?: RoleKey,
  isImpersonating?: boolean,
  licenseEnabled?: boolean
): NavItem[] {
  if (!modules) return [];
  return items.filter((it) => {
    // OEM W5:授权入口只在 license 启用的私有化实例上出现
    if (it.licenseOnly && !licenseEnabled) return false;
    // 1004 O5:直营专属项(更新记录)在渠道 / 中性品牌下不出现
    if (it.directBrandOnly && !showsReleaseNotes(getBrand())) return false;
    // 产品注册表(2026-06-04) — 仅代客登录时可见,无 module_key
    if (it.impersonateOnly) return !!isImpersonating;
    if (it.hideWhenImpersonating && isImpersonating) return false;
    // V11.4 (2026-05-12) — scope 白名单优先;设了 scopes 就先按 scope gate
    if (it.scopes && scope && !it.scopes.includes(scope)) return false;
    // V22 (2026-05-15) — role 白名单(用于 STAFF 隐藏 Billing/套餐入口)
    if (it.roles && role && !it.roles.includes(role)) return false;
    if (!it.moduleKey) return true;
    const m = modules[it.moduleKey];
    return !!m && m.page !== "none";
  });
}

/**
 * 导航项高亮判定 — 路径段边界匹配(条6①,2026-07-05)。
 * 旧实现 `currentPath.startsWith(item.url)` 无边界:`/advisor-memory-audit` 命中 `/advisor-memory`
 * 前缀 → 「记忆审计」路由激活时「军师记忆」父项也被点亮。改为「精确相等 或 以 url+'/' 开头」,
 * 既保子路由点亮父项(/ai-haibao/create → /ai-haibao),又不误命中兄弟前缀。
 */
export function isNavItemActive(currentPath: string, url: string): boolean {
  return currentPath === url || currentPath.startsWith(url + "/");
}
